<script setup lang="ts">
import { computed } from "vue";
import { RuleSetRule, isRuleSetRule } from "@/modules/rule_engine/ruleEngineClasses";
import GroupTree from "@/modules/rule_engine/components/GroupTree.vue";

/**
 * Structure of the model, as a navigable tree.
 *
 * Rules are dragged onto a node to move them into that group, which is why
 * `MoveSelectionDialog` no longer exists — the destinations are all on screen.
 */
const props = withDefaults(
	defineProps<{
		node: RuleSetRule;
		selectedUuid: string | null;
		level?: number;
		isRoot?: boolean;
	}>(),
	{ level: 0, isRoot: false }
);

const emit = defineEmits<{
	select: [uuid: string];
	"drop-rules": [payload: { uuids: string[]; targetUuid: string }];
}>();

/** Only groups are nodes; leaf rules live in the detail pane. */
const childGroups = computed(() =>
	(props.node.rule_set?.rules ?? []).filter(isRuleSetRule)
);

const leafCount = computed(
	() => (props.node.rule_set?.rules ?? []).filter((r) => !isRuleSetRule(r)).length
);

const isSelected = computed(() => props.selectedUuid === props.node.uuid);

const DRAG_TYPE = "application/x-rule-uuids";

const carriesRules = (event: DragEvent) =>
	Array.from(event.dataTransfer?.types ?? []).includes(DRAG_TYPE);

const onDragOver = (event: DragEvent) => {
	if (!carriesRules(event)) return;

	event.preventDefault();
	event.stopPropagation();
	event.dataTransfer!.dropEffect = "move";
	(event.currentTarget as HTMLElement).classList.add("over");
};

const onDragLeave = (event: DragEvent) => {
	(event.currentTarget as HTMLElement).classList.remove("over");
};

const onDrop = (event: DragEvent) => {
	if (!carriesRules(event)) return;

	event.preventDefault();
	event.stopPropagation();
	(event.currentTarget as HTMLElement).classList.remove("over");

	const raw = event.dataTransfer?.getData(DRAG_TYPE);
	if (!raw) return;

	const uuids: string[] = JSON.parse(raw);
	if (uuids.length > 0) {
		emit("drop-rules", { uuids, targetUuid: props.node.uuid });
	}
};
</script>

<template>
	<details :open="isRoot || childGroups.length > 0" class="tree-node">
		<summary
			class="node"
			:class="{ selected: isSelected }"
			:style="{ paddingLeft: `${level * 0.85 + 0.5}rem` }"
			@click.prevent="emit('select', node.uuid)"
			@dragover="onDragOver"
			@dragleave="onDragLeave"
			@drop="onDrop"
		>
			<v-icon size="13" class="mr-2 twisty" v-if="childGroups.length > 0">
				mdi-chevron-right
			</v-icon>
			<span v-else class="twisty-spacer" />

			<v-icon size="13" class="mr-2 node-icon">
				{{ isRoot ? "mdi-file-tree" : "mdi-folder-outline" }}
			</v-icon>

			<span class="node-name">{{ node.rule_set.name || "Unnamed" }}</span>

			<span class="count" v-if="leafCount > 0">{{ leafCount }}</span>
		</summary>

		<div v-if="childGroups.length > 0">
			<group-tree
				v-for="child in childGroups"
				:key="child.uuid"
				:node="child"
				:selected-uuid="selectedUuid"
				:level="level + 1"
				@select="(uuid) => emit('select', uuid)"
				@drop-rules="(payload) => emit('drop-rules', payload)"
			/>
		</div>
	</details>
</template>

<style scoped lang="scss">
.tree-node {
	--indent-guide: rgba(0, 0, 0, 0.08);
}

.node {
	display: flex;
	align-items: center;
	gap: 0.1rem;
	min-height: 32px;
	padding-right: 0.5rem;
	border-radius: 4px;
	cursor: pointer;
	user-select: none;
	list-style: none;
	font-size: 0.875rem;
	transition: background-color 120ms ease;

	&::-webkit-details-marker {
		display: none;
	}

	&:hover {
		background-color: rgba(0, 0, 0, 0.04);
	}

	&.selected {
		background-color: rgba(var(--v-theme-primary), 0.12);
		font-weight: 600;
	}

	&.over {
		outline: 2px dashed rgb(var(--v-theme-primary));
		outline-offset: -2px;
		background-color: rgba(var(--v-theme-primary), 0.08);
	}
}

.twisty {
	opacity: 0.55;
	transition: transform 150ms ease;
}

details[open] > .node > .twisty {
	transform: rotate(90deg);
}

.twisty-spacer {
	display: inline-block;
	width: 13px;
	margin-right: 0.5rem;
}

.node-icon {
	opacity: 0.55;
}

.node-name {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.count {
	margin-left: auto;
	font-size: 0.7rem;
	opacity: 0.55;
	padding-left: 0.5rem;
}
</style>
